import React, { useState, useEffect } from 'react';
import {
    ArrowLeft,
    BadgeHelp,
    Bot,
    BrainCircuit,
    CheckCircle2,
    ChevronDown,
    Headset,
    Loader2,
    MessageSquare,
    PencilLine,
    Pin,
    Plus,
    Search,
    Star,
    Store,
    Trash2,
    User,
    X,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import ConfirmDialog from '../../components/ConfirmDialog';
import Select from '../../components/Select';
import API from '../../utils/api';
import useChatWindow from '../../hooks/useChatWindow';
import ChatComposer from '../../components/chat/ChatComposer';
import ChatAttachment from '../../components/chat/ChatAttachment';
import { onAdminRefresh } from '../../utils/adminSocket';
import { DeletedText, ForwardedTag, MessageFlags, PinnedBanner, Reactions, ReplyQuote } from '../../components/chat/MessageParts';
import useMessageMenu from '../../components/chat/useMessageMenu';
import { ForwardDialog, SavedMessagesDialog } from '../../components/chat/ChatDialogs';
import toast from 'react-hot-toast';
import './SupportChat.css';

const FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'needs', label: 'Needs reply' },
    { id: 'unread', label: 'Unread' },
    { id: 'unassigned', label: 'Unassigned' },
    { id: 'assigned', label: 'Assigned' },
];


const SERVER_URL = (API.defaults?.baseURL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const ATTACHMENT_LABEL = { image: 'Photo', video: 'Video', audio: 'Audio', document: 'File' };
const previewOf = (text, attachment) => (text && text.trim()) || (attachment?.url ? ATTACHMENT_LABEL[attachment.kind] || 'File' : '');
const uploadChatFile = async (file, onProgress) => {
    const data = new FormData();
    data.append('file', file);
    const res = await API.post('/chat-uploads', data, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: e => onProgress?.(Math.min(100, Math.round((e.loaded / (e.total || file.size || 1)) * 100))) });
    return res.data.attachment;
};

const PAGE_SIZE = 10;
const EMPTY_ENTRY = { question: '', answer: '', audience: 'all', keywords: '' };
const nameOf = c => c.user?.name || c.user?.businessName || 'User';
const timeOf = at => new Date(at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const SupportChat = () => {
    const [tab, setTab] = useState('chats');
    const [chats, setChats] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [activeChat, setActiveChat] = useState(null);
    const [teach, setTeach] = useState(true);
    const [filter, setFilter] = useState('all');
    const [chatLimit, setChatLimit] = useState(PAGE_SIZE);   // conversations shown; "Load more" adds 10
    const [brainLimit, setBrainLimit] = useState(PAGE_SIZE);  // bot answers shown

    // Bot brain
    const [entries, setEntries] = useState([]);
    const [brainSearch, setBrainSearch] = useState('');
    const [ownerDrafts, setOwnerDrafts] = useState({}); // answers being typed for the "Questions for you" list
    const [entryForm, setEntryForm] = useState(null);      // null = closed, otherwise the form values
    const [editingId, setEditingId] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);

    const fetchChats = async () => {
        try {
            const res = await API.get('/support/admin/all');
            setChats(res.data.chats || []);
        } catch (err) { toast.error('Failed to load support chats'); }
    };

    const fetchOneChat = async (id) => {
        try {
            const res = await API.get(`/support/admin/${id}`);
            setActiveChat(res.data.chat);
            fetchChats();
        } catch (err) { }
    };

    const fetchEntries = async () => {
        try {
            const res = await API.get('/support/admin/brain');
            setEntries(res.data.entries || []);
        } catch (err) { toast.error('Failed to load the bot brain'); }
    };

    useEffect(() => {
        fetchChats();
        API.get('/admin/admins')
            .then(res => setAdmins((res.data.admins || []).filter(a => a.canHandleSupport)))
            .catch(() => { });
        const offRefresh = onAdminRefresh(fetchChats);
        const timer = setInterval(fetchChats, 10000); // new user questions show up without a refresh
        return () => { clearInterval(timer); offRefresh(); };
    }, []);

    useEffect(() => { if (selectedId) fetchOneChat(selectedId); }, [selectedId]);
    useEffect(() => { setReplyTarget(null); setEditTarget(null); }, [selectedId]);
    useEffect(() => { if (tab === 'brain') fetchEntries(); }, [tab]);

    // The open conversation is loaded 20 messages at a time, scrolling up or down for more.
    const selectedChat = chats.find(c => c._id === selectedId);
    const chat = useChatWindow({
        fetchPage: params => API.get(`/support/admin/${selectedId}/messages`, { params }).then(r => r.data),
        chatKey: selectedId,
        version: selectedChat?.lastMessageAt,
    });

    // ── Message actions: reply, react, edit, delete, star, pin, forward ──
    const [replyTarget, setReplyTarget] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [forwardItem, setForwardItem] = useState(null);
    const [savedKind, setSavedKind] = useState(null);

    const performAction = async (item, action, payload = {}) => {
        try {
            const res = await API.post(`/support/admin/${selectedId}/messages/${item._id}/action`, { action, ...payload });
            if (res.data.removed) chat.removeItem(item._id);
            else chat.patchItem(item._id, res.data.item);
            if (action === 'delete' && replyTarget?._id === item._id) setReplyTarget(null);
            return true;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not update the message');
            return false;
        }
    };

    const menu = useMessageMenu({
        onAction: performAction,
        onReply: item => { setEditTarget(null); setReplyTarget(item); },
        onEdit: item => { setReplyTarget(null); setEditTarget(item); },
        onForward: setForwardItem,
        isOwn: item => item.from === 'admin',
        canForward: item => item.from === 'admin', // only your own replies can be forwarded to another customer
        attachmentBase: SERVER_URL,
    });

    const authorOf = (from) => (from === 'admin' ? 'You' : from === 'bot' ? 'Assistant' : (activeChat ? nameOf(activeChat) : 'Customer'));

    const forwardTo = async (target) => {
        const item = forwardItem;
        setForwardItem(null);
        try {
            await API.post(`/support/admin/${target.id}/reply`, { text: item.text, attachment: item.attachment, forwarded: true, teach: false });
            toast.success(`Forwarded to ${target.name}`);
            fetchChats();
        } catch (err) {
            toast.error('Could not forward');
        }
    };

    const fetchSaved = async (kind) => {
        const res = await API.get(`/support/admin/${selectedId}/messages`, { params: { filter: kind } });
        return res.data.items || [];
    };

    // Sends the admin's reply (text and / or an uploaded file). "Teach the bot" only applies to the text.
    const handleReply = async ({ text, attachment, editing }) => {
        if (editing) return performAction(editing, 'edit', { text });
        try {
            const res = await API.post(`/support/admin/${selectedId}/reply`, { text, attachment, teach, replyToId: replyTarget?._id });
            setReplyTarget(null);
            setActiveChat(res.data.chat);
            setTeach(true);
            fetchChats();
            chat.jumpToLatest();
            return true;
        } catch (err) {
            toast.error('Failed to send');
            return false;
        }
    };

    const handleAssign = async (adminId) => {
        try {
            const res = await API.put(`/support/admin/${selectedId}/assign`, { adminId: adminId || null });
            setActiveChat(res.data.chat);
            toast.success(res.data.message);
            fetchChats();
        } catch (err) { toast.error('Failed to assign'); }
    };

    const handleClose = async () => {
        try {
            await API.put(`/support/admin/${selectedId}/close`);
            toast.success('Marked as resolved');
            fetchChats();
            fetchOneChat(selectedId);
        } catch (err) { toast.error('Failed'); }
    };

    const saveEntry = async (event) => {
        event.preventDefault();
        try {
            if (editingId) await API.put(`/support/admin/brain/${editingId}`, entryForm);
            else await API.post('/support/admin/brain', entryForm);
            toast.success(editingId ? 'Answer updated' : 'Answer added to the brain');
            setEntryForm(null);
            setEditingId(null);
            fetchEntries();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
    };

    const toggleEntry = async (entry) => {
        try {
            await API.put(`/support/admin/brain/${entry._id}`, { isActive: !entry.isActive });
            fetchEntries();
        } catch (err) { toast.error('Failed'); }
    };

    const confirmDeleteEntry = async () => {
        const id = pendingDelete;
        setPendingDelete(null);
        if (!id) return;
        try {
            await API.delete(`/support/admin/brain/${id}`);
            toast.success('Removed from the brain');
            fetchEntries();
        } catch (err) { toast.error('Failed'); }
    };

    const startEdit = (entry) => {
        setEntryForm({ question: entry.question, answer: entry.answer, audience: entry.audience, keywords: (entry.keywords || []).join(', ') });
        setEditingId(entry._id);
    };

    const needsCount = chats.filter(c => c.awaitingAdmin).length;
    const visibleChats = chats.filter(c => {
        if (filter === 'needs') return c.awaitingAdmin;
        if (filter === 'unread') return c.unreadByAdmin;
        if (filter === 'assigned') return !!c.assignedTo;
        if (filter === 'unassigned') return !c.assignedTo;
        return true;
    });
    const ownerQuestions = entries.filter(e => e.needsAnswer);
    const visibleEntries = entries.filter(e => !e.needsAnswer && (
        `${e.question} ${e.answer} ${(e.keywords || []).join(' ')}`.toLowerCase().includes(brainSearch.toLowerCase())));

    const saveOwnerAnswer = async (e) => {
        const text = (ownerDrafts[e._id] || '').trim();
        if (!text) { toast.error('Type the answer first'); return; }
        try {
            await API.put(`/support/admin/brain/${e._id}`, { answer: text });
            toast.success('Saved. The chatbot can use it now');
            setOwnerDrafts(d => { const n = { ...d }; delete n[e._id]; return n; });
            fetchEntries();
        } catch (err) { toast.error(err.response?.data?.message || 'Could not save'); }
    };

    const roleLabel = c => (c.user?.role === 'service'
        ? <><Store size={12} /> Service Provider</>
        : <><User size={12} /> Member</>);

    return (
        <div className="support-layout">
            <Sidebar />
            <div className="admin-content support-content">
                <Navbar title={<span className="support-nav-title"><MessageSquare size={18} /> Support Chat</span>} />

                <main className="support-page">
                    <div className="support-tabs" role="tablist">
                        <button type="button" role="tab" aria-selected={tab === 'chats'} className={tab === 'chats' ? 'is-active' : ''} onClick={() => setTab('chats')}>
                            <Headset size={15} /> Conversations
                            {needsCount > 0 && <span className="support-tabs__badge">{needsCount}</span>}
                        </button>
                        <button type="button" role="tab" aria-selected={tab === 'brain'} className={tab === 'brain' ? 'is-active' : ''} onClick={() => setTab('brain')}>
                            <BrainCircuit size={15} /> Bot brain
                        </button>
                    </div>

                    {tab === 'chats' && (
                        <div className={`support-grid${activeChat ? ' is-chat-open' : ''}`}>
                            <div className="support-list-panel">
                                <div className="support-filters">
                                    {FILTERS.map(f => (
                                        <button key={f.id} type="button" className={filter === f.id ? 'is-active' : ''} onClick={() => { setFilter(f.id); setChatLimit(PAGE_SIZE); }}>{f.label}</button>
                                    ))}
                                </div>
                                <div className="support-list">
                                    {visibleChats.slice(0, chatLimit).map(c => {
                                        const last = c.messages[c.messages.length - 1];
                                        return (
                                            <button type="button" key={c._id} className={`support-item${selectedId === c._id ? ' is-selected' : ''}`} onClick={() => setSelectedId(c._id)}>
                                                <span className="support-item__top">
                                                    <span className="support-item__name">{nameOf(c)}</span>
                                                    {c.awaitingAdmin
                                                        ? <span className="support-chip is-warn"><BadgeHelp size={11} /> Needs reply</span>
                                                        : c.unreadByAdmin && <i className="support-dot" aria-label="Unread" />}
                                                </span>
                                                <span className="support-item__meta">{roleLabel(c)}{c.assignedTo && ` · ${c.assignedTo.name}`}</span>
                                                <span className="support-item__preview">
                                                    {last ? `${last.from === 'bot' ? 'Bot: ' : last.from === 'admin' ? 'You: ' : ''}${previewOf(last.text, last.attachment)}` : 'No messages yet'}
                                                </span>
                                            </button>
                                        );
                                    })}
                                    {visibleChats.length === 0 && <div className="support-empty">No conversations here</div>}
                                    {visibleChats.length > chatLimit && (
                                        <div className="support-more">
                                            <button type="button" onClick={() => setChatLimit(n => n + PAGE_SIZE)}>
                                                Load more ({visibleChats.length - chatLimit} remaining)
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="support-chat-panel">
                                {!activeChat ? (
                                    <div className="support-empty support-empty--center">
                                        <span><MessageSquare size={26} /></span>
                                        <p>Select a conversation to view</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="support-chat-header">
                                            <button type="button" className="support-back" onClick={() => { setSelectedId(null); setActiveChat(null); }} aria-label="Back to conversations"><ArrowLeft size={18} /></button>
                                            <div className="support-chat-header__who">
                                                <div className="support-chat-header__name">{nameOf(activeChat)}</div>
                                                <div className="support-chat-header__meta">{activeChat.user?.mobile} · {roleLabel(activeChat)}</div>
                                            </div>
                                            <div className="support-chat-header__actions">
                                                <button type="button" className="support-header-icon" onClick={() => setSavedKind('pinned')} aria-label="Pinned messages" title="Pinned messages"><Pin size={16} /></button>
                                                <button type="button" className="support-header-icon" onClick={() => setSavedKind('starred')} aria-label="Starred messages" title="Starred messages"><Star size={16} /></button>
                                                <div className="support-assign">
                                                    <Select
                                                        value={activeChat.assignedTo?._id || ''}
                                                        onChange={handleAssign}
                                                        options={[{ value: '', label: 'Unassigned' }, ...admins.map(a => ({ value: a._id, label: a.name }))]}
                                                        allowEmpty={false}
                                                        ariaLabel="Assign conversation"
                                                    />
                                                </div>
                                                {activeChat.status === 'open'
                                                    ? <button type="button" className="support-resolve" onClick={handleClose}><CheckCircle2 size={14} /> Resolve</button>
                                                    : <span className="support-resolved">Resolved</span>}
                                            </div>
                                        </div>

                                        <PinnedBanner items={chat.items} onOpen={() => setSavedKind('pinned')} />
                                        <div className="support-messages" ref={chat.scrollRef} onScroll={chat.onScroll}>
                                            {chat.loadingOlder && <div className="chat-loader"><Loader2 size={14} /> Loading older messages…</div>}
                                            {chat.loading && <div className="chat-loader"><Loader2 size={14} /> Loading messages…</div>}
                                            {chat.items.map((m, i) => (
                                                <div key={m._id || i} data-chat-id={m._id} className={`support-row${m.from === 'admin' ? ' is-admin' : ''}`}>
                                                    {m.from === 'bot' && <span className="support-avatar is-bot"><Bot size={14} /></span>}
                                                    <div className="msg-bubble-wrap" {...menu.bind(m)}>
                                                        <div className={`support-bubble is-${m.from}`}>
                                                            {m.from === 'bot' && <div className="support-bubble__label">Assistant</div>}
                                                            <ForwardedTag item={m} />
                                                            <ReplyQuote reply={m.replyTo} author={authorOf(m.replyTo?.from)} />
                                                            {m.deleted ? <DeletedText /> : (
                                                                <>
                                                                    <ChatAttachment attachment={m.attachment} base={SERVER_URL} />
                                                                    {m.text && <div className="support-bubble__text">{m.text}</div>}
                                                                </>
                                                            )}
                                                            {m.unanswered && <div className="support-chip is-warn support-bubble__flag"><BadgeHelp size={11} /> Bot couldn't answer</div>}
                                                            <time><MessageFlags item={m} />{timeOf(m.at)}</time>
                                                        </div>
                                                            <Reactions reactions={m.reactions} onToggle={emoji => performAction(m, 'react', { emoji })} end={m.from === 'admin'} />
                                                        <button type="button" className="msg-menu-btn" onClick={e => menu.openFrom(m, e)} aria-label="Message options"><ChevronDown size={14} /></button>
                                                    </div>
                                                </div>
                                            ))}
                                            {chat.loadingNewer && <div className="chat-loader"><Loader2 size={14} /> Loading newer messages…</div>}
                                        </div>

                                        {activeChat.awaitingAdmin && (
                                            <div className="support-teach-bar">
                                                <label className="support-teach">
                                                    <input type="checkbox" checked={teach} onChange={e => setTeach(e.target.checked)} />
                                                    <BrainCircuit size={14} /> Teach the bot this answer so it can reply automatically next time
                                                </label>
                                            </div>
                                        )}
                                        <ChatComposer
                                            key={activeChat._id}
                                            placeholder="Type a reply..."
                                            onSend={handleReply}
                                            replyTo={replyTarget}
                                            replyLabel={replyTarget ? authorOf(replyTarget.from) : ''}
                                            onCancelReply={() => setReplyTarget(null)}
                                            editing={editTarget}
                                            onCancelEdit={() => setEditTarget(null)}
                                            uploadFile={uploadChatFile}
                                            onError={message => toast.error(message)}
                                            fileBase={SERVER_URL}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {tab === 'brain' && (
                        <section className="brain">
                            <div className="brain-toolbar">
                                <div>
                                    <h2>Bot brain</h2>
                                    <p>{entries.length} answers · taught automatically when you reply to a question the bot couldn't answer</p>
                                </div>
                                <div className="brain-toolbar__actions">
                                    <label className="brain-search"><Search size={14} />
                                        <input placeholder="Search answers..." value={brainSearch} onChange={e => { setBrainSearch(e.target.value); setBrainLimit(PAGE_SIZE); }} />
                                    </label>
                                    <button type="button" className="brain-add" onClick={() => { setEditingId(null); setEntryForm({ ...EMPTY_ENTRY }); }}>
                                        <Plus size={16} /> Add answer
                                    </button>
                                </div>
                            </div>

                            {entryForm && (
                                <form className="brain-form" onSubmit={saveEntry}>
                                    <div className="brain-form__head">
                                        <h3>{editingId ? 'Edit answer' : 'Add an answer'}</h3>
                                        <button type="button" onClick={() => { setEntryForm(null); setEditingId(null); }} aria-label="Close form"><X size={16} /></button>
                                    </div>
                                    <label>Question
                                        <input value={entryForm.question} onChange={e => setEntryForm({ ...entryForm, question: e.target.value })} placeholder="e.g. How do I change my phone number?" required />
                                    </label>
                                    <label>Answer
                                        <textarea rows={4} value={entryForm.answer} onChange={e => setEntryForm({ ...entryForm, answer: e.target.value })} placeholder="What the bot should reply" required />
                                    </label>
                                    <div className="brain-form__split">
                                        <div className="brain-field">
                                            <span>Who is it for?</span>
                                            <Select
                                                value={entryForm.audience}
                                                onChange={audience => setEntryForm({ ...entryForm, audience })}
                                                options={[
                                                    { value: 'all', label: 'Everyone' },
                                                    { value: 'member', label: 'Members only' },
                                                    { value: 'service', label: 'Service providers only' },
                                                ]}
                                                allowEmpty={false}
                                                ariaLabel="Who is it for?"
                                            />
                                        </div>
                                        <label>Other ways to ask it (comma separated)
                                            <textarea rows={3} style={{ width: '100%', resize: 'vertical' }} value={entryForm.keywords} onChange={e => setEntryForm({ ...entryForm, keywords: e.target.value })} placeholder="change number, update mobile, mobile number maathanum" />
                                        </label>
                                    </div>
                                    <div className="brain-form__foot">
                                        <button type="button" className="secondary" onClick={() => { setEntryForm(null); setEditingId(null); }}>Cancel</button>
                                        <button type="submit" className="primary">{editingId ? 'Save changes' : 'Add to brain'}</button>
                                    </div>
                                </form>
                            )}

                            {ownerQuestions.length > 0 && (
                                <div className="owner-questions">
                                    <h3>Questions for you ({ownerQuestions.length})</h3>
                                    <p>Only you know these. Type the answer and save; the chatbot starts using it at once and until then hands the question to the team.</p>
                                    {ownerQuestions.map(q => (
                                        <div key={q._id} className="owner-question">
                                            <strong>{q.question}</strong>
                                            {q.hint && <small>{q.hint}</small>}
                                            <textarea rows={2} placeholder="Your answer..." value={ownerDrafts[q._id] || ''} onChange={ev => setOwnerDrafts(d => ({ ...d, [q._id]: ev.target.value }))} />
                                            <button type="button" className="primary" onClick={() => saveOwnerAnswer(q)}>Save answer</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="brain-list">
                                {visibleEntries.slice(0, brainLimit).map(e => (
                                    <article key={e._id} className={`brain-card${e.isActive ? '' : ' is-off'}`}>
                                        <div className="brain-card__head">
                                            <h3>{e.question}</h3>
                                            <div className="brain-card__chips">
                                                <span className="support-chip">{e.audience === 'all' ? 'Everyone' : e.audience === 'member' ? 'Members' : 'Service providers'}</span>
                                                <span className={`support-chip ${e.source === 'admin' ? 'is-learned' : ''}`}>{e.source === 'admin' ? 'Learned' : 'Built-in'}</span>
                                                <span className="support-chip">Used {e.hits}×</span>
                                            </div>
                                        </div>
                                        <p>{e.answer}</p>
                                        {e.keywords?.length > 0 && <div className="brain-card__kw">Also matches: {e.keywords.join(', ')}</div>}
                                        <div className="brain-card__actions">
                                            <button type="button" onClick={() => startEdit(e)}><PencilLine size={13} /> Edit</button>
                                            <button type="button" onClick={() => toggleEntry(e)}>{e.isActive ? 'Disable' : 'Enable'}</button>
                                            <button type="button" className="danger" onClick={() => setPendingDelete(e._id)} aria-label="Delete answer"><Trash2 size={13} /></button>
                                        </div>
                                    </article>
                                ))}
                                {visibleEntries.length === 0 && <div className="support-empty">No answers found</div>}
                                {visibleEntries.length > brainLimit && (
                                    <div className="support-more support-more--wide">
                                        <button type="button" onClick={() => setBrainLimit(n => n + PAGE_SIZE)}>
                                            Load more ({visibleEntries.length - brainLimit} remaining)
                                        </button>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}
                </main>
            </div>

            {menu.element}
            {forwardItem && (
                <ForwardDialog
                    targets={chats.filter(c => c._id !== selectedId).map(c => ({ id: c._id, name: nameOf(c), subtitle: c.user?.role === 'service' ? 'Service Provider' : 'Member' }))}
                    onPick={forwardTo}
                    onClose={() => setForwardItem(null)}
                />
            )}
            {savedKind && (
                <SavedMessagesDialog
                    kind={savedKind}
                    fetchItems={fetchSaved}
                    labelFor={item => authorOf(item.from)}
                    onRemove={item => performAction(item, savedKind === 'starred' ? 'star' : 'pin')}
                    onClose={() => setSavedKind(null)}
                />
            )}

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Remove this answer?"
                message="The bot will stop using it, and unknown questions like it will be sent to admins again."
                confirmLabel="Remove"
                danger
                onConfirm={confirmDeleteEntry}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
};

export default SupportChat;
